import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '@/store'
import type { ProduccionCabecera, PlanificacionSemanalRow } from '@/types'
import { fmt, fmtUds, parseDate } from '@/utils/planningFormatters'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { cuentaComoRotura } from '@/utils/estadoMaterial'

/**
 * Ancho de la barra lateral, en píxeles, tal y como lo declara `Sidebar.tsx`
 * (`w-[64px]` plegada, `w-[200px]` desplegada).
 *
 * La pestaña de alertas y el panel se anclan al BORDE DERECHO de esa barra: si
 * el número no coincide con el real, la pestaña se mete debajo de la barra y
 * pierde parte de su icono. Estaba en 56 con la barra midiendo 64, y así se
 * comía 8 px. Si en `Sidebar.tsx` cambian esos anchos, hay que cambiarlos aquí.
 */
const SIDEBAR_W = { collapsed: 64, expanded: 200 } as const

export function AlertsDrawer({ producciones, planRows, prodsConRotura, dark, onNavigate }: {
  producciones: ProduccionCabecera[]
  planRows: PlanificacionSemanalRow[]
  prodsConRotura: Set<string>
  dark: boolean
  onNavigate: (prodid: string) => void
}) {
  const { t } = useTranslation()
  const { sidebarCollapsed } = useAppStore()
  const { isMobile } = useBreakpoint()
  const [open, setOpen] = useState(false)
  const fmtArt = useFmtArticulo()

  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d }, [])
  const in14  = useMemo(() => { const d = new Date(today); d.setDate(today.getDate() + 14); return d }, [today])

  const roturaAlerts = useMemo(() => {
    return producciones
      .filter(p => prodsConRotura.has(p.produccion))
      .map(p => {
        const rows = planRows.filter(r => r.prodid === p.produccion && cuentaComoRotura(r) && r.pendiente > 0)
        return {
          prodid: p.produccion,
          nombre: p.nombreArticulo || fmtArt(p.articulo),
          rutaPrincipal: p.rutaPrincipal || 'SIN CLASIFICAR',
          nComponents: rows.length,
          totalPendiente: rows.reduce((s, r) => s + Math.max(0, r.pendiente), 0),
        }
      })
      .filter(a => a.nComponents > 0)
  }, [producciones, planRows, prodsConRotura, fmtArt])

  const odtAlerts = useMemo(() => {
    return producciones
      .filter(p => {
        if (!prodsConRotura.has(p.produccion)) return false
        const f = parseDate(p.fechaFin)
        return f && f >= today && f <= in14 && Number(p.unidadesPendientes) > 0
      })
      .map(p => {
        const f = parseDate(p.fechaFin)!
        const daysLeft = Math.round((f.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
        return {
          prodid: p.produccion,
          nombre: p.nombreArticulo || fmtArt(p.articulo),
          daysLeft,
          pendiente: Number(p.unidadesPendientes),
        }
      })
      .sort((a, b) => a.daysLeft - b.daysLeft)
  }, [producciones, prodsConRotura, today, in14, fmtArt])

  const totalCount = roturaAlerts.length + odtAlerts.length
  const hasRed     = roturaAlerts.length > 0

  const bg     = dark ? 'bg-slate-900'    : 'bg-white'
  const bdr    = dark ? 'border-white/10' : 'border-slate-200'
  const txtM   = dark ? 'text-slate-50'   : 'text-slate-900'
  const txtMut = dark ? 'text-slate-400'  : 'text-slate-500'
  const rowBdr = dark ? 'border-white/5'  : 'border-slate-100'

  function handleClick(prodid: string) {
    setOpen(false)
    onNavigate(prodid)
  }

  return (
    <>
      <button
        onClick={() => setOpen(v => !v)}
        className={`fixed z-40 flex flex-col items-center gap-1.5 px-1.5 py-3 rounded-r-xl shadow-lg border-r border-t border-b transition-all cursor-pointer ${
          dark
            ? 'bg-slate-800 border-white/12 text-slate-300 hover:bg-slate-700'
            : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50 shadow-md'
        }`}
        style={{ left: isMobile ? 0 : (sidebarCollapsed ? SIDEBAR_W.collapsed : SIDEBAR_W.expanded), top: '50%', transform: 'translateY(-50%)', transition: 'left 200ms' }}
        title={t('planning.alerts.buttonTitle')}
        aria-label={t('planning.alerts.buttonAriaLabel')}
      >
        <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M8 2a4.5 4.5 0 0 0-4.5 4.5V9L2 11h12l-1.5-2V6.5A4.5 4.5 0 0 0 8 2Z" strokeLinejoin="round" />
          <path d="M6.5 13a1.5 1.5 0 0 0 3 0" strokeLinecap="round" />
        </svg>
        {totalCount > 0 ? (
          <span className={`text-[9px] font-black tabular-nums rounded-full w-5 h-5 flex items-center justify-center shrink-0 ${
            hasRed ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'
          }`}>
            {totalCount > 9 ? '9+' : totalCount}
          </span>
        ) : (
          <span className={`text-[9px] rounded-full w-5 h-5 flex items-center justify-center shrink-0 ${
            dark ? 'bg-emerald-900/60 text-emerald-400' : 'bg-emerald-100 text-emerald-500'
          }`}>✓</span>
        )}
        <span
          className={`text-[8px] font-bold uppercase tracking-widest shrink-0 ${txtMut}`}
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          {t('planning.alerts.tab')}
        </span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-[1px]" onClick={() => setOpen(false)} />
          <div
            className={`relative z-10 flex flex-col h-full shadow-2xl border-r ${bg} ${bdr}`}
            style={{ marginLeft: isMobile ? 0 : (sidebarCollapsed ? SIDEBAR_W.collapsed : SIDEBAR_W.expanded), width: isMobile ? '100%' : 360 }}
          >
            <div className={`flex items-center justify-between px-5 py-4 border-b shrink-0 ${bdr}`}>
              <div>
                <div className={`text-[10px] font-bold uppercase tracking-widest ${txtMut} mb-0.5`}>{t('planning.alerts.center')}</div>
                <div className={`text-[15px] font-black ${txtM}`}>
                  {totalCount === 0
                    ? t('planning.alerts.noAlerts')
                    : t('planning.alerts.alertCount', { count: totalCount })}
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className={`w-7 h-7 flex items-center justify-center rounded-lg text-[13px] transition-colors cursor-pointer ${
                  dark ? 'text-slate-400 hover:bg-white/10 hover:text-slate-100'
                       : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'
                }`}
                aria-label={t('planning.alerts.closePanel')}
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M1 1l12 12M13 1L1 13"/>
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto min-h-0">
              {totalCount === 0 && (
                <div className={`py-16 text-center ${txtMut}`}>
                  <div className={`text-[38px] mb-3 ${dark ? 'text-emerald-500/30' : 'text-emerald-400/40'}`}>✓</div>
                  <div className={`text-[13px] font-bold ${dark ? 'text-slate-300' : 'text-slate-600'}`}>{t('planning.alerts.allGood')}</div>
                  <div className={`text-[11px] mt-1 ${txtMut}`}>{t('planning.alerts.allGoodSub')}</div>
                </div>
              )}

              {roturaAlerts.length > 0 && (
                <>
                  <div className={`px-5 py-2.5 flex items-center gap-2 sticky top-0 z-10 border-b ${bg} ${bdr}`}>
                    <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                    <span className={`text-[10px] font-bold uppercase tracking-widest ${dark ? 'text-red-400' : 'text-red-600'}`}>{t('planning.alerts.activeBreaks')}</span>
                    <span className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${dark ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-600'}`}>
                      {roturaAlerts.length}
                    </span>
                  </div>
                  {roturaAlerts.map(a => (
                    <button key={a.prodid} onClick={() => handleClick(a.prodid)}
                      className={`w-full text-left px-5 py-3 border-b transition-colors cursor-pointer ${
                        dark ? `${rowBdr} hover:bg-red-950/25` : `border-slate-100 hover:bg-red-50/60`
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className={`text-[12px] font-bold ${txtM} truncate`}>{a.prodid}</div>
                          <div className={`text-[11px] ${txtMut} truncate`}>{a.nombre}</div>
                          <div className={`text-[10px] mt-0.5 ${dark ? 'text-slate-600' : 'text-slate-400'} truncate`}>{a.rutaPrincipal}</div>
                        </div>
                        <div className="shrink-0 text-right ml-2">
                          <div className={`text-[11px] font-bold ${dark ? 'text-red-400' : 'text-red-600'}`}>{a.nComponents} mat.</div>
                          <div className={`text-[10px] tabular-nums ${dark ? 'text-red-500' : 'text-red-400'}`}>+{fmt(a.totalPendiente, 0)}u</div>
                        </div>
                      </div>
                      <div className={`mt-1.5 text-[10px] font-medium flex items-center gap-1 ${dark ? 'text-sky-500/70' : 'text-sky-500'}`}>
                        <span>→</span><span>{t('planning.alerts.goToProduction')}</span>
                      </div>
                    </button>
                  ))}
                </>
              )}

              {odtAlerts.length > 0 && (
                <>
                  <div className={`px-5 py-2.5 flex items-center gap-2 sticky top-0 z-10 border-b ${bg} ${bdr}`}>
                    <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                    <span className={`text-[10px] font-bold uppercase tracking-widest ${dark ? 'text-amber-400' : 'text-amber-600'}`}>{t('planning.alerts.deliveriesAtRisk')}</span>
                    <span className={`text-[9px] ${dark ? 'text-slate-500' : 'text-slate-400'} ml-1`}>≤14 días</span>
                    <span className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${dark ? 'bg-amber-900/50 text-amber-400' : 'bg-amber-100 text-amber-600'}`}>
                      {odtAlerts.length}
                    </span>
                  </div>
                  {odtAlerts.map(a => (
                    <button key={a.prodid} onClick={() => handleClick(a.prodid)}
                      className={`w-full text-left px-5 py-3 border-b transition-colors cursor-pointer ${
                        dark ? `${rowBdr} hover:bg-amber-950/25` : `border-slate-100 hover:bg-amber-50/60`
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className={`text-[12px] font-bold ${txtM} truncate`}>{a.prodid}</div>
                          <div className={`text-[11px] ${txtMut} truncate`}>{a.nombre}</div>
                        </div>
                        <div className="shrink-0 text-right ml-2">
                          <div className={`text-[14px] font-black tabular-nums leading-none ${
                            a.daysLeft <= 3 ? (dark ? 'text-red-400' : 'text-red-600') : (dark ? 'text-amber-400' : 'text-amber-600')
                          }`}>{a.daysLeft}d</div>
                          <div className={`text-[9px] ${txtMut}`}>{t('planning.alerts.daysLeft')}</div>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <div className={`flex-1 h-1 rounded-full overflow-hidden ${dark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                          <div
                            className={`h-full rounded-full transition-all ${a.daysLeft <= 3 ? 'bg-red-400' : 'bg-amber-400'}`}
                            style={{ width: `${Math.max(5, Math.round((1 - a.daysLeft / 14) * 100))}%` }}
                          />
                        </div>
                        <span className={`text-[10px] tabular-nums shrink-0 ${txtMut}`}>{fmtUds(a.pendiente)} {t('planning.alerts.unitsPending')}</span>
                      </div>
                      <div className={`mt-1.5 text-[10px] font-medium flex items-center gap-1 ${dark ? 'text-sky-500/70' : 'text-sky-500'}`}>
                        <span>→</span><span>{t('planning.alerts.goToProduction')}</span>
                      </div>
                    </button>
                  ))}
                </>
              )}
            </div>

            <div className={`px-5 py-3 border-t shrink-0 ${bdr}`}>
              <div className={`text-[10px] ${txtMut}`}>
                {roturaAlerts.length > 0 && (
                  <span>{t('planning.alerts.breaksSummary', { count: roturaAlerts.length })}{odtAlerts.length > 0 ? ' · ' : ''}</span>
                )}
                {odtAlerts.length > 0 && <span>{t('planning.alerts.deliveriesSummary', { count: odtAlerts.length })}</span>}
                {totalCount === 0 && <span>{t('planning.alerts.updatedNow')}</span>}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
